<script lang="ts" setup>
    import RothcoCta from "./RothcoCta.vue";
    import CategoryLauncherFullWidthCarouselRow from "./CategoryLauncherFullWidthCarouselRow.vue";
    import { computed } from "vue";
    import type { Cta } from "@/helpers/builder-init/index";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";
    import * as themeColors from "@assets/styles/colors.module.scss";
    import useImageOptimization from "@/helpers/composables/useImageOptimization";
    import RightArrow from "@icons/right-arrow.svg";
    interface LauncherItem {
        headline: string;
        image: string;
        image_alt: string;
        cta: Cta;
    }

    interface LauncherRow {
        launcherItems: LauncherItem[];
    }
    let props = defineProps({
        headline: {
            type: String,
            default: ""
        },
        launcherRows: {
            type: Array as PropType<Array<LauncherRow[]>>,
            default() {
                return [];
            }
        },
        use_gradient: {
            type: Boolean,
            default: false
        },
        gradient_color: {
            type: String,
            default: 'rothcoBlack'
        },
        togglePadding: {
            type: Boolean,
            default: false
        },
        textBelow: {
            type: Boolean,
            default: false
        },
        useCarouselControls: {
            type: Boolean,
            default: true
        },
    });

    const cssVars = computed(() => {
        if(props.use_gradient){
            let r,g,b;
            let gradientColor = themeColors[props.gradient_color] ?? themeColors['rothcoBlack'];
            if(gradientColor){
                r = parseInt(gradientColor.substring(1, 3), 16);
                g = parseInt(gradientColor.substring(3, 5), 16);
                b = parseInt(gradientColor.substring(5, 7), 16);
            }
            return {
                "--category-launcher-gradient": gradientColor ? `${r}, ${g}, ${b}` : undefined
            };
        }else{
            return [];
        }
    });

    let breakpoints = useRothcoBreakpoints();
    let breakpointIsMobile = computed(()=>{
        return breakpoints.smaller('laptop').value;
    });

    let { optimize } = useImageOptimization();

    function getRowClass(row: LauncherRow) {
        return {
            'paletted-row': props.headline.length == 0,
            'palette-three-items': row.launcherItems.length == 1 || row.launcherItems.length == 2 || row.launcherItems.length == 3,
            'palette-four-items': row.launcherItems.length == 4,
            'palette-five-items': row.launcherItems.length > 4
        };
    }

    function rowBindings(row: LauncherRow) {
        const rowClass = getRowClass(row);
        if (props.useCarouselControls) {
            return {
                rowClass,
                isMobile: breakpointIsMobile.value,
                itemCount: row.launcherItems.length
            };
        }
        return { class: rowClass };
    }

</script>
<template>
    <section :class="{'palette-items-container': true, 'category-launcher-fullwidth-container': true, 'alternative-padding': props.togglePadding}" :style="cssVars">
        <span class="headline-l palette-title" v-if="headline.length > 0" role="heading" aria-level="2">{{
            headline
        }}</span>
        <component
            :is="props.useCarouselControls ? CategoryLauncherFullWidthCarouselRow : 'div'"
            v-for="(row, rowIndex) in launcherRows"
            :key="rowIndex"
            v-bind="rowBindings(row)"
        >
            <component
                :is="item.cta && !breakpointIsMobile ? RothcoCta : 'div'"
                v-bind="item.cta ? getCtaLink(item.cta) : {}"
                ctaType="link"
                html-slotted
                v-for="(item, itemIndex) in row.launcherItems"
                :key="itemIndex"
                :class="{
                    'pti-item': true,
                    third: row.launcherItems.length == 3,
                    half: row.launcherItems.length == 2,
                    'text-below': props.textBelow
                }"
                :aria-label="item.headline"
                :disabled="!item.cta"
            >
                <img
                    loading="lazy"
                    :src="optimize(item.image)"
                    :alt="item.image_alt"
                    class="pti-image"
                    id="category_image"
                />
                <div class="launcher-cta" v-if="props.textBelow && item.headline">
                    <div class="text-content">
                        <div class="headline-xs" id="headline_text">
                            {{ item.headline }}
                        </div>
                        <RightArrow
                            alt="arrow right"
                            class="carret"
                            aria-label="hidden"
                            aria-hidden="true"
                        />
                    </div>
                    <div class="fill"/>
                </div>
                <RothcoCta v-else-if="!breakpointIsMobile && item.headline" :ctaType="item.cta?.type ?? 'button'" :class="(item?.cta?.style ?? 'secondary dark')">
                    {{ item.headline }}
                </RothcoCta>
                <RothcoCta v-else-if="item.headline" :href="item.cta ? getCtaLink(item.cta).href : '#'" :ctaType="item.cta?.type ?? 'button'" :class="(item?.cta?.style ?? 'secondary dark')">
                    {{ item.headline }}
                </RothcoCta>
            </component>
        </component>
    </section>
</template>
